<template>
  <base-view id="support-portal-topic">
    <div class="w-full page-fill bg-gray-lighter">
      <div class="flex flex-col items-center px-6 pt-10 pb-16">
        <div class="w-full max-w-2xl">
          <div class="flex flex-wrap items-center gap-2 mb-6 text-sm">
            <router-link
              to="/support-portal"
              class="font-semibold text-primary hover:underline"
            >
              Home
            </router-link>
            <template v-if="topic">
              <span class="text-gray-400">/</span>
              <span class="text-gray-700">{{ topic.name }}</span>
            </template>
          </div>

          <div v-if="zendeskError" class="text-center">
            <p class="mb-4 text-gray-700">
              We couldn't load this topic. Please check your connection and try
              again.
            </p>
            <button
              type="button"
              @click="fetchZendeskData"
              class="px-5 py-2 text-sm font-bold text-white rounded-full bg-primary"
            >
              Try again
            </button>
          </div>
          <div v-else-if="loading" class="animate-pulse">
            <div class="w-full mb-6 h-40 bg-gray-200 rounded-xl sm:h-48"></div>
            <div class="w-1/2 h-7 mb-2 bg-gray-200 rounded"></div>
            <div class="w-3/4 h-4 mb-8 bg-gray-200 rounded"></div>
            <div class="flex flex-col gap-2.5">
              <div
                v-for="n in 4"
                :key="n"
                class="h-16 bg-gray-200 rounded-lg"
              ></div>
            </div>
          </div>
          <div v-else-if="!topic">
            <p class="mb-4 text-gray-700">We couldn't find that topic.</p>
            <router-link
              to="/support-portal"
              class="font-semibold text-primary hover:underline"
            >
              Back to Support Home
            </router-link>
          </div>
          <div v-else>
            <img
              v-if="topic.image"
              :src="topic.image.src"
              :alt="topic.name"
              loading="lazy"
              class="w-full mb-6 h-40 rounded-xl sm:h-48"
              :style="{
                objectFit: topic.image.size,
                objectPosition: topic.image.position,
                transform: topic.image.scale
                  ? `scale(${topic.image.scale})`
                  : undefined
              }"
            />
            <TopicIcon v-else :icon="topic.icon" class="mb-4" />
            <h1 class="mb-2 text-3xl font-extrabold text-gray-900">
              {{ topic.name }}
            </h1>
            <p class="mb-8 text-base text-gray-700">{{ topic.blurb }}</p>
            <div class="flex flex-col gap-2.5">
              <template v-if="section && section.subsections">
                <ArticleListItem
                  v-for="article in section.ungroupedArticles"
                  :key="article.id"
                  :title="article.title"
                  :excerpt="excerptFor(article)"
                  :featured="!!article.promoted"
                  :to="`/support-portal/articles/${article.id}`"
                />
                <template v-for="subsection in section.subsections">
                  <div
                    :key="subsection.name + '-header'"
                    class="flex items-center gap-3 pt-2"
                  >
                    <span
                      class="text-xs font-bold tracking-widest uppercase text-primary"
                      >{{ subsection.name }}</span
                    >
                    <div class="flex-1 h-px bg-gray-200" />
                  </div>
                  <ArticleListItem
                    v-for="article in subsection.articles"
                    :key="article.id"
                    :title="article.title"
                    :excerpt="excerptFor(article)"
                    :featured="!!article.promoted"
                    :to="`/support-portal/articles/${article.id}`"
                  />
                </template>
              </template>

              <template v-else>
                <ArticleListItem
                  v-for="article in articles"
                  :key="article.id"
                  :title="article.title"
                  :excerpt="excerptFor(article)"
                  :featured="!!article.promoted"
                  :to="`/support-portal/articles/${article.id}`"
                />
                <p v-if="!articles.length" class="py-4 text-sm text-gray-700">
                  No articles in this topic yet.
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </base-view>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BaseView from "@/components/layout/BaseView.vue";
import TopicIcon from "./components/TopicIcon.vue";
import ArticleListItem from "./components/ArticleListItem.vue";
import { excerpt } from "./utils";
import { imageForSection, iconForSection } from "./topicVisuals";

export default {
  name: "SupportRedesignTopic",
  components: { BaseView, TopicIcon, ArticleListItem },
  data() {
    return {
      topicId: parseInt(this.$route.params.topicId)
    };
  },
  watch: {
    $route(to) {
      this.topicId = parseInt(to.params.topicId);
    },
    topic: {
      immediate: true,
      handler(topic) {
        if (topic) {
          document.title = `${topic.name} | Support | Rocky Mountain Reserve`;
        }
      }
    }
  },
  computed: {
    ...mapGetters(["allZendeskData", "zendeskError"]),
    loading() {
      return !(this.allZendeskData && this.allZendeskData.sections);
    },
    section() {
      return this.$store.getters.sectionById(this.topicId);
    },
    topic() {
      if (!this.section) return null;
      return {
        name: this.section.name,
        blurb:
          this.section.description && this.section.description.trim()
            ? this.section.description
            : `Articles about ${this.section.name}.`,
        image: imageForSection(this.section.id),
        icon: iconForSection(this.section.id)
      };
    },
    articles() {
      return (this.section && this.section.articles) || [];
    }
  },
  methods: {
    ...mapActions(["fetchZendeskData"]),
    excerptFor(article) {
      return excerpt(article.body);
    }
  }
};
</script>

<style scoped>
/* Matches base-view's own min-height so the background fills the page even
   when there's little content, instead of the gray-lighter wrapper
   stopping short and showing plain white below. */
.page-fill {
  min-height: calc(100vh - 10rem);
}
</style>
